/**
 * Shared section-JS facade (Part 4b). Single enforcement point for the
 * Proxy-based Document facade that wraps every section instance's JS. Both the
 * export path (generators.js `generateExportScripts`) and the preview path
 * (renderer/section-scripts.js `generateScopedScript`) are thin wrappers over
 * `buildFacadeIIFE`, so preview/export scoping can never diverge.
 *
 * Two root causes are fixed here (see plan Part 4):
 *  1. Un-scoped class/attr queries. The old facade only rewrote `#sectionId`
 *     references; `document.querySelector('.cs-faq-item')` still hit the WHOLE
 *     document, so two FAQ sections double-bound every item. Now every
 *     top-level selector part is STRICTLY scoped to the instance root
 *     (`#<exportId>` / `[data-pb-instance]`), with NO fallback-to-document on
 *     empty results.
 *  2. Every page runs every instance's IIFE (one sitewide bundle). A
 *     root-presence gate (first statement: `if (!__getSectionRoot()) return;`)
 *     makes an off-page instance a no-op so it never registers listeners.
 *
 * Escape hatch: `jsScope:'document'` (or the legacy DOCUMENT_SCOPE_SECTION_IDS
 * set) restores the old loose behavior for the cooperative multi-part
 * special-codestitch sets that intentionally query across sections.
 *
 * Global patterns are deliberately untouched: document.addEventListener,
 * documentElement, matchMedia, localStorage, window.*, timers, observers. Only
 * document.querySelector/All/getElementById and document.body are proxied.
 */

// Sections whose embedded JS is allowed to run un-scoped (cooperative sets whose
// selectors intentionally reach across sections). New saved projects carry the
// `jsScope:'document'` field; this constant covers already-saved projects whose
// embedded section data predates the field.
export const DOCUMENT_SCOPE_SECTION_IDS = new Set([
  // Real section ids of the cooperative modal set (stitch-9008 / -1 / -2).
  'special-9008',
  'cs-backdrop',
  'cs-modal',
  // Catalog keys too, so an instance keyed by either resolves.
  'stitch-9008',
  'stitch-9008-1',
  'stitch-9008-2'
]);

/** True when a section should use the loose (document-wide) facade. */
export function sectionUsesDocumentScope(section) {
  if (!section) return false;
  if (section.jsScope === 'document') return true;
  return (
    DOCUMENT_SCOPE_SECTION_IDS.has(section.sectionId) ||
    DOCUMENT_SCOPE_SECTION_IDS.has(section.catalogKey)
  );
}

/**
 * The ONE transform between a facade's JS and a document that embeds it.
 *
 * An HTML parser ends a `<script>` at the first literal `</script`, whatever the
 * JS syntax says, and treats `<!--` as the start of a comment — so those two
 * sequences are escaped and nothing else is. Both replacements are idempotent
 * (`<\/script` no longer contains `</script`), which is what lets the facade and
 * the tag-writing caller each apply it without the two compounding.
 *
 * Its deleted sibling, `escapeForScript`, additionally doubled backslashes and
 * escaped backticks and `${`. That is the escaping for interpolation into a
 * TEMPLATE LITERAL, and the facade does not interpolate into one — `${escaped}`
 * below is evaluated when this module runs, on a literal whose source is fixed.
 * So the doubling was pure damage, and it was not theoretical: measured over the
 * corpus on 2026-09-07, **10 of the 8,742 sections carrying JS produced an
 * export bundle that does not parse** (`stitch-2455`'s
 * `` `translateX(-${d}px)` `` became `` \`translateX(-\${d}px)\` ``, a
 * SyntaxError at statement level). Every page links ONE `sections.js`, so each of
 * those killed the JS of every section on the site, not merely its own.
 */
export function escapeForInlineScript(js) {
  return String(js || '')
    .replace(/<\/script/gi, '<\\/script')
    .replace(/<!--/g, '<\\!--');
}

/**
 * Build the facade IIFE for one section instance.
 * @param {object} opts
 * @param {'export'|'preview'} opts.mode
 * @param {string} opts.js - the raw section JS
 * @param {string} opts.sectionId - original section id (for #sectionId rewrites)
 * @param {string} [opts.exportId] - export mode: the instance's unique export id
 * @param {string} [opts.instanceId] - preview mode: the instance id (data-pb-instance)
 * @param {boolean} [opts.documentScope] - loose (old) facade for cooperative sets
 * @param {'external-js'|'inline-html'} [opts.embedding] - where the text is going
 * @returns {string} the IIFE source
 */
export function buildFacadeIIFE({
  mode,
  js,
  sectionId,
  exportId,
  instanceId,
  documentScope = false,
  idRenames = null,
  // WHERE this text will live, which is the only thing that decides what has to
  // be escaped — and is independent of `mode`, which decides SCOPING. The two
  // were fused before AG-4 and that is how the export path acquired
  // template-literal escaping it never needed: `mode: 'export'` reaches an
  // external `sections.js` from three exporters AND an inline `<script>` from
  // the saved-preview bundler.
  //
  // `inline-html` is the default because the failure modes are not symmetric.
  // Escaping `</script` in a file nobody parses as HTML is inert; NOT escaping
  // it in a document ends the script tag early and puts the rest of the JS on
  // the page as text.
  embedding = 'inline-html'
}) {
  const isExport = mode === 'export';
  // Canonical-id descendant renames (Workstream A4). Sorted keys so the
  // emitted facade is deterministic (export-lock run-twice). When the map is
  // empty/absent the emitted text is BYTE-IDENTICAL to the historical facade.
  const hasIdRenames = idRenames instanceof Map && idRenames.size > 0;
  const idMapJson = hasIdRenames
    ? JSON.stringify(
        Object.fromEntries(
          [...idRenames.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        )
      )
    : null;
  const idMapDecl = hasIdRenames ? `\n  var __idMap = ${idMapJson};` : '';
  const idMapLookup = hasIdRenames ? '\n    id = __idMap[id] || id;' : '';
  const rwIdRenames = hasIdRenames
    ? `
    for (var __k in __idMap) {
      var __ke = __k.replace(/[.*+?^\${}()|[\\]\\\\]/g, '\\\\$&');
      s = s.replace(new RegExp('#' + __ke + '(?=[\\\\s.:\\\\[,>+~]|$)', 'g'), '#' + __idMap[__k]);
    }`
    : '';
  const scopeSelector = isExport ? `#${exportId}` : `[data-pb-instance="${instanceId}"]`;
  const label = isExport ? `// ${exportId}` : `// ${instanceId}: ${sectionId}`;
  // External JS passes through UNCHANGED. There is no HTML parser downstream, so
  // there is nothing an escape could protect against — only source it could
  // corrupt.
  const escaped = embedding === 'external-js' ? String(js || '') : escapeForInlineScript(js);
  const strict = documentScope ? 'false' : 'true';

  const rootExpr = isExport
    ? '__rd.getElementById(__eid)'
    : "(__rd.querySelector(__scope + ' [data-pb-section]') || __rd.querySelector(__scope))";
  const getByIdSelfExpr = isExport ? '__rd.getElementById(__eid)' : '__rd.querySelector(__scope)';

  const storageBlock = isExport
    ? ''
    : `
  function __makeMemoryStorage() {
    var data = Object.create(null);
    return {
      get length() { return Object.keys(data).length; },
      key: function(i) { return Object.keys(data)[Number(i)] || null; },
      getItem: function(k) { k = String(k); return Object.prototype.hasOwnProperty.call(data, k) ? data[k] : null; },
      setItem: function(k, v) { data[String(k)] = String(v); },
      removeItem: function(k) { delete data[String(k)]; },
      clear: function() { data = Object.create(null); }
    };
  }
  function __getSafeStorage(name) {
    try {
      var storage = __sw[name];
      var testKey = '__pb_storage_test__';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return storage;
    } catch (err) { return __makeMemoryStorage(); }
  }
  var localStorage = __getSafeStorage('localStorage');
  var sessionStorage = __getSafeStorage('sessionStorage');`;

  const windowExtras = isExport
    ? ''
    : `
      if (p === 'localStorage') return localStorage;
      if (p === 'sessionStorage') return sessionStorage;`;
  const globalAliases = isExport ? '' : '\n  var self = window;\n  var globalThis = window;';

  const errorMsg = isExport
    ? "console.error('[Section JS error in ' + __eid + ']:', e);"
    : `console.error('[PB] Section JS error in ' + __sid + ' (' + ${JSON.stringify(instanceId)} + '):', e);`;

  return `${label}
(function(__rd, __sw) {
  var __sid = ${JSON.stringify(sectionId)};
  var __eid = ${JSON.stringify(exportId || instanceId)};
  var __scope = ${JSON.stringify(scopeSelector)};
  var __strict = ${strict};${idMapDecl}

  // Root-presence gate (first statement): an off-page instance never runs, so it
  // never registers document/window listeners — kills cross-page contamination.
  if (!__getSectionRoot()) return;

  function __getSectionRoot() { return ${rootExpr}; }

  // Rewrite the section's own #sectionId references to the instance anchor.
  function __rwId(s) {
    var esc = __sid.replace(/[.*+?^\${}()|[\\]\\\\]/g, '\\\\$&');
    var re = new RegExp('#' + esc + '(?=[\\\\s.:\\\\[,>+~]|$)', 'g');
    ${hasIdRenames ? `s = s.replace(re, __scope);${rwIdRenames}\n    return s;` : 'return s.replace(re, __scope);'}
  }
  // Split a selector list on TOP-LEVEL commas only (not inside [] () or quotes).
  function __splitTop(sel) {
    var parts = [], depth = 0, buf = '', q = null;
    for (var i = 0; i < sel.length; i++) {
      var c = sel[i];
      if (q) { buf += c; if (c === q) q = null; continue; }
      if (c === '"' || c === "'") { q = c; buf += c; continue; }
      if (c === '[' || c === '(') depth++;
      else if (c === ']' || c === ')') { if (depth > 0) depth--; }
      if (c === ',' && depth === 0) { parts.push(buf); buf = ''; continue; }
      buf += c;
    }
    if (buf.length) parts.push(buf);
    return parts;
  }
  // Strictly scope one top-level selector part to the instance root.
  function __scopePart(raw) {
    var p = raw.trim();
    if (!p) return '';
    var low = p.toLowerCase();
    if (low === 'body' || low.indexOf('body') === 0 || low.indexOf('html') === 0) return p; // global
    if (p.charAt(0) === '#') return p;                    // id-prefixed → instance-unique/global
    if (p.indexOf(__scope) === 0) return p;               // already anchored (preview attr scope)
    var f = p.charAt(0);
    if (f === '.' || f === '[' || f === ':') {
      return __scope + p + ', ' + __scope + ' ' + p;      // self OR descendant
    }
    return __scope + ' ' + p;                             // tag/other → descendant only
  }
  function __rw(sel) {
    if (typeof sel !== 'string') return sel;
    var rewritten = __rwId(sel);
    if (!__strict) return rewritten;                      // loose (document-scope) mode
    var out = [], parts = __splitTop(rewritten);
    for (var i = 0; i < parts.length; i++) {
      var scoped = __scopePart(parts[i]);
      if (scoped) out.push(scoped);
    }
    return out.join(', ');
  }
  // Selector-SYNTAX errors (not empty results) fall back to the un-scoped query.
  function __safeQuery(isAll, sel) {
    var fn = isAll ? __rd.querySelectorAll.bind(__rd) : __rd.querySelector.bind(__rd);
    try { return fn(__rw(sel)); } catch (e) { return fn(sel); }
  }
${storageBlock}
  var __bodyProxy = null;
  var __bodyClassListProxy = null;
  function __isBodySelector(s) { return typeof s === 'string' && s.trim().toLowerCase() === 'body'; }
  function __mirrorScrollClass(enabled) {
    var root = __getSectionRoot();
    if (!root || !root.classList) return;
    root.classList.toggle('scroll', !!enabled);
  }
  function __argsIncludeScroll(args) {
    for (var i = 0; i < args.length; i++) { if (args[i] === 'scroll') return true; }
    return false;
  }
  // LG-4: the editor can PIN the scrolled header state so it can be designed
  // against. The parser injects a scroll listener into every template nav that
  // removes the scroll class below 100px, so a pin has to survive that listener
  // rather than race it — and this proxy is already the one place the token is
  // intercepted. Removals are suppressed while pinned; ADDS never are, so a real
  // scroll past the threshold is still a no-op rather than a fight.
  function __scrollPinned() {
    try { return !!window.__PB_SCROLL_PINNED__; } catch (e) { return false; }
  }
  function __getBodyProxy(body) {
    if (!body) return body;
    if (__bodyProxy) return __bodyProxy;
    __bodyProxy = new Proxy(body, {
      get: function(bt, bp) {
        if (bp === 'classList') {
          if (__bodyClassListProxy) return __bodyClassListProxy;
          __bodyClassListProxy = new Proxy(bt.classList, {
            get: function(cl, cp) {
              if (cp === 'add') return function() { var r = cl.add.apply(cl, arguments); if (__argsIncludeScroll(arguments)) __mirrorScrollClass(true); return r; };
              if (cp === 'remove') return function() { if (__argsIncludeScroll(arguments) && __scrollPinned()) return undefined; var r = cl.remove.apply(cl, arguments); if (__argsIncludeScroll(arguments)) __mirrorScrollClass(false); return r; };
              if (cp === 'toggle') return function(token, force) { if (token === 'scroll' && __scrollPinned() && !(arguments.length > 1 && force)) return true; var r = cl.toggle.apply(cl, arguments); if (token === 'scroll') __mirrorScrollClass(arguments.length > 1 ? !!force : r); return r; };
              if (cp === 'replace') return function(oldToken, newToken) { if (oldToken === 'scroll' && __scrollPinned()) return false; var r = cl.replace.apply(cl, arguments); if (oldToken === 'scroll') __mirrorScrollClass(false); if (newToken === 'scroll' && r) __mirrorScrollClass(true); return r; };
              var cv = cl[cp]; return typeof cv === 'function' ? cv.bind(cl) : cv;
            }
          });
          return __bodyClassListProxy;
        }
        var bv = bt[bp]; return typeof bv === 'function' ? bv.bind(bt) : bv;
      }
    });
    return __bodyProxy;
  }

  // Strict-mode getElementById prefers an element INSIDE the instance root.
  // Repeated sections keep their authored child ids (e.g. the 1605 modal's
  // cs-close-1605), so a document-wide getElementById would always return
  // instance A's copy — binding instance B's listeners inside A. The attribute
  // selector sidesteps CSS ident-escaping for arbitrary id strings. Ids not
  // found in this instance (page-global ids like #cs-expanded) fall through to
  // the real document lookup, unchanged.
  function __getById(t, id) {
    if (id === __sid) return ${getByIdSelfExpr};${idMapLookup}
    if (__strict) {
      var root = __getSectionRoot();
      if (root && root.querySelector) {
        var local = null;
        try { local = root.querySelector('[id="' + String(id).replace(/(["\\\\])/g, '\\\\$1') + '"]'); } catch (e) { local = null; }
        if (local) return local;
      }
    }
    return t.getElementById(id);
  }

  var document = new Proxy(__rd, {
    get: function(t, p) {
      if (p === 'body') return __getBodyProxy(t.body);
      if (p === 'querySelector') return function(s) { return __isBodySelector(s) ? __getBodyProxy(t.body) : __safeQuery(false, s); };
      if (p === 'querySelectorAll') return function(s) { return __safeQuery(true, s); };
      if (p === 'getElementById') return function(id) { return __getById(t, id); };
      var v = t[p]; return typeof v === 'function' ? v.bind(t) : v;
    }
  });

  var window = new Proxy(__sw, {
    get: function(t, p) {
      if (p === 'document') return document;${windowExtras}
      var v = t[p]; return typeof v === 'function' ? v.bind(t) : v;
    }
  });${globalAliases}

  try {
    ${escaped}
  } catch(e) {
    ${errorMsg}
  }
})(document, ${isExport ? 'self' : 'window'});`;
}
